<script setup>
import { ref, computed, nextTick } from 'vue';

const props = defineProps({
  id: { type: String, required: true },
  data: { type: Object, required: true },
  selected: { type: Boolean, default: false },
});

const emit = defineEmits(['remove', 'change']);

const isEditing = ref(false);
const textareaRef = ref(null);

const note = computed(() => props.data.block || {});
const text = computed(() => note.value.options?.text || '');

const color = computed(() => {
  const c = note.value.options?.color || 'yellow';
  const map = {
    yellow: {
      bg: 'rgb(var(--amber-4))',
      border: 'rgb(var(--amber-7))',
      text: 'rgb(var(--amber-12))',
      toolbar: 'rgb(var(--amber-6))',
      placeholder: 'rgb(var(--amber-9))',
    },
    blue: {
      bg: 'rgb(var(--blue-4))',
      border: 'rgb(var(--blue-7))',
      text: 'rgb(var(--blue-12))',
      toolbar: 'rgb(var(--blue-6))',
      placeholder: 'rgb(var(--blue-9))',
    },
    green: {
      bg: 'rgb(var(--teal-4))',
      border: 'rgb(var(--teal-7))',
      text: 'rgb(var(--teal-12))',
      toolbar: 'rgb(var(--teal-6))',
      placeholder: 'rgb(var(--teal-9))',
    },
    pink: {
      bg: 'rgb(var(--ruby-4))',
      border: 'rgb(var(--ruby-7))',
      text: 'rgb(var(--ruby-12))',
      toolbar: 'rgb(var(--ruby-6))',
      placeholder: 'rgb(var(--ruby-9))',
    },
  };
  return map[c] || map.yellow;
});

const startEdit = async () => {
  isEditing.value = true;
  await nextTick();
  textareaRef.value?.focus();
};

const stopEdit = () => {
  isEditing.value = false;
};

const onInput = event => {
  if (!note.value.options) note.value.options = {};
  note.value.options.text = event.target.value;
  emit('change');
};

const cycleColor = () => {
  const colors = ['yellow', 'blue', 'green', 'pink'];
  const current = note.value.options?.color || 'yellow';
  const idx = colors.indexOf(current);
  if (!note.value.options) note.value.options = {};
  note.value.options.color = colors[(idx + 1) % colors.length];
  emit('change');
};
</script>

<template>
  <div
    class="dc-sticky"
    :class="{ 'dc-sticky-selected': selected }"
    :style="{
      background: color.bg,
      borderColor: color.border,
    }"
    @dblclick="startEdit"
  >
    <!-- toolbar: color cycle + delete -->
    <div class="dc-sticky-toolbar">
      <button
        type="button"
        class="dc-sticky-btn"
        :style="{ color: color.text }"
        @click.stop="cycleColor"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="13.5" cy="6.5" r="2.5"/><path d="M17.4 10.1a7 7 0 1 1-4.8-4.8"/><circle cx="7.5" cy="10.5" r="2" fill="currentColor" opacity=".3"/><circle cx="10.5" cy="15.5" r="2" fill="currentColor" opacity=".3"/>
        </svg>
      </button>
      <button
        type="button"
        class="dc-sticky-btn"
        :style="{ color: color.text }"
        @click.stop="emit('remove', id)"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
        </svg>
      </button>
    </div>

    <!-- content -->
    <textarea
      v-if="isEditing"
      ref="textareaRef"
      :value="text"
      class="dc-sticky-textarea"
      :style="{ color: color.text }"
      @input="onInput"
      @blur="stopEdit"
      @keydown.escape="stopEdit"
    />
    <p
      v-else
      class="dc-sticky-text"
      :style="{ color: text ? color.text : color.placeholder }"
    >
      {{ text || $t('FLOWS.STICKY.PLACEHOLDER') }}
    </p>
  </div>
</template>

<style scoped>
.dc-sticky {
  min-width: 200px;
  max-width: 280px;
  min-height: 120px;
  padding: 0.75rem;
  border-radius: 6px;
  border: 2px solid;
  box-shadow: 3px 3px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.15s ease;
  cursor: grab;
}
.dc-sticky:hover {
  box-shadow: 3px 5px 16px rgba(0, 0, 0, 0.12);
}
.dc-sticky-selected {
  box-shadow: 0 0 0 2px rgb(var(--blue-9)), 3px 5px 16px rgba(0, 0, 0, 0.12);
}

.dc-sticky-toolbar {
  display: flex;
  justify-content: flex-end;
  gap: 0.25rem;
  margin-bottom: 0.375rem;
  opacity: 0;
  transition: opacity 0.15s ease;
}
.dc-sticky:hover .dc-sticky-toolbar {
  opacity: 1;
}

.dc-sticky-btn {
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.15s ease;
}
.dc-sticky-btn:hover {
  background: rgba(0, 0, 0, 0.08);
}

.dc-sticky-text {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 60px;
}

.dc-sticky-textarea {
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  width: 100%;
  min-height: 60px;
  resize: both;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
}
</style>
